/**
 * useMyPurchases — TanStack Query hook backing the Me → Purchases sub-page.
 *
 * Endpoint: GET /api/v1/me/purchases?limit=&cursor=
 * Response envelope: { items: PurchaseItem[], nextCursor: string | null }
 *
 * For P1.6 we expose the first page only; cursor pagination is wired in the
 * hook but the UI consumes a single page (no infinite scroll yet).
 */

import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../lib/api-client';
import { useAuth } from '../store/auth';
import type { ResultRef } from '../lib/result-route';

export interface PurchaseItem {
  id: string;
  skuCode: string;
  skuName: string;
  status: 'completed' | 'processing' | 'failed';
  thumbUrl: string | null;
  teaserText: string | null;
  createdAt: string;
  /** Where to open this purchase's result, or null if it has no in-app viewer. */
  result: ResultRef | null;
}

interface Envelope {
  items: PurchaseItem[];
  nextCursor: string | null;
}

export function useMyPurchases(limit = 20): {
  data: PurchaseItem[] | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const jwt = useAuth((s) => s.jwt);

  const query = useQuery<PurchaseItem[], Error>({
    queryKey: ['me-purchases', limit],
    queryFn: async () => {
      if (!jwt) throw new Error('not_authenticated');
      const res = await apiFetch(`/api/v1/me/purchases?limit=${limit}`, jwt);
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`me_purchases_failed: ${res.status} ${body}`);
      }
      const env = (await res.json()) as Envelope;
      return env.items;
    },
    enabled: !!jwt,
    staleTime: 30_000,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}
